import { gql, useLazyQuery } from '@apollo/client';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from '../../components/Button';
import Header from '../../components/Header';
import Input from '../../components/Input';
import InspectLinkedText from '../../components/InspectLinkedText';
import Table from '../../components/Table';

import type {
  AdminInspectObjectQuery,
  AdminInspectObjectQueryVariables,
} from './__generated__/ObjectInspectorScene.gql';

const ADMIN_INSPECT_OBJECT = gql`
  query AdminInspectObject($id: String!) {
    adminInspectObject(id: $id) {
      primary {
        type
        id
        data
      }
      alternateMatches {
        type
        id
      }
      related {
        type
        count
        objects {
          type
          id
          data
        }
      }
    }
  }
`;

type InspectorResult = NonNullable<AdminInspectObjectQuery['adminInspectObject']>;
type InspectedObject = InspectorResult['primary'];
type RelatedGroup = InspectorResult['related'][number];
type AlternateMatch = InspectorResult['alternateMatches'][number];

/**
 * Object Inspector admin tool. Operator pastes a UUID, or the eight-character
 * short form the rest of the UI truncates ids to; the resolver searches every
 * inspectable table for a matching row, returns it as the "primary", and walks
 * tenancy anchors (`user_id` / `family_id`) to bucket every related row across
 * other tables, pinning any foreign-key parents the registry declares first in
 * their group. The UI renders the primary as a key/value table at the top and
 * each related group as a collapsible section below.
 *
 * Designed for "I have an id from a log line / Sentry breadcrumb / DB query
 * — what is it?" debugging. Returns an empty state for unknown ids and
 * surfaces redacted values (`[REDACTED]`) verbatim so operators see exactly
 * what the resolver suppressed. A `?id=` link opens on that id, and every
 * lookup writes its id back, so the address bar names the row on screen.
 *
 * `?id=` is the single source of truth for what is on screen, so every way of
 * reaching a row goes through it: typing one, picking an alternate match, and
 * following an id rendered inside a result. That last one is why the param is
 * followed rather than read once at mount — the scene links its own ids back to
 * itself, so a click lands on the route it is already mounted on and would
 * otherwise move the address bar and nothing else.
 */
const ObjectInspectorScene = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramId = searchParams.get('id')?.trim() ?? '';
  const [inputId, setInputId] = React.useState(() => paramId);
  const [submittedId, setSubmittedId] = React.useState<string | null>(null);
  // Related groups start collapsed by default — operators usually scan group
  // counts first and only expand the group they care about. Tracking the
  // expanded set (rather than the collapsed set) makes the default trivial
  // and survives result refreshes without per-fetch reset logic.
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());

  const [inspect, { data, loading, error }] = useLazyQuery<AdminInspectObjectQuery, AdminInspectObjectQueryVariables>(
    ADMIN_INSPECT_OBJECT,
    { fetchPolicy: 'network-only' }
  );

  /**
   * Asks for a row by putting it in the address bar. The fetch belongs to the
   * effect below rather than to this function, so a typed lookup and a clicked
   * id take exactly the same path.
   */
  const inspectId = (rawId: string) => {
    const trimmed = rawId.trim();
    if (trimmed.length === 0) {
      return;
    }
    setInputId(trimmed);
    setSearchParams({ id: trimmed }, { replace: true });
  };

  /**
   * The effect below fires on a change of `?id=`, so resubmitting the id
   * already in the address bar would be a no-op. An explicit press is how a
   * failed lookup is retried, so it asks for that fetch itself.
   */
  const handleInspect = () => {
    const trimmed = inputId.trim();
    if (trimmed.length === 0) {
      return;
    }
    if (trimmed === paramId) {
      setExpandedGroups(new Set());
      void inspect({ variables: { id: trimmed } });
      return;
    }
    inspectId(trimmed);
  };

  React.useEffect(() => {
    if (paramId.length === 0) {
      // Back at the bare tool, so the row leaves with the id that named it.
      if (submittedId !== null) {
        setSubmittedId(null);
        setInputId('');
        setExpandedGroups(new Set());
      }
      return;
    }
    // Guarding on the id already fetched is what keeps this from looping:
    // `inspectId` writes the same value back that the effect then reads.
    if (paramId === submittedId) {
      return;
    }
    setInputId(paramId);
    setSubmittedId(paramId);
    setExpandedGroups(new Set());
    void inspect({ variables: { id: paramId } });
  }, [paramId, submittedId, inspect]);

  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // The lazy query holds its last result, so the row is gated on the id that
  // named it rather than outliving a return to the bare tool.
  const result = submittedId === null ? null : (data?.adminInspectObject ?? null);

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Object inspector</h1>

        {/* Lookup row */}
        <div className="border-border-primary flex items-end gap-2 border-b pb-4">
          <Input
            className="w-[28rem]"
            label="Object id"
            placeholder="UUID or its first 8 characters, Plaid item / account id, message id…"
            value={inputId}
            onChange={setInputId}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInspect();
              }
            }}
            hint="Searches internal UUIDs — whole or just their first 8 characters — and external ids (Plaid `item_…`, `acc_…`, etc.)."
          />
          <Button
            color="primary"
            size="sm"
            onClick={handleInspect}
            isLoading={loading}
            isDisabled={inputId.trim().length === 0}
          >
            Inspect
          </Button>
        </div>

        {/* Result */}
        {error && (
          <div className="text-fg-danger-primary text-xs">Failed to inspect object. Check the id and try again.</div>
        )}

        {!loading && !error && submittedId !== null && result === null && (
          <div className="text-fg-tertiary text-xs italic">
            No row found with id <span className="font-mono">{submittedId}</span> in any inspectable table.
          </div>
        )}

        {result !== null && (
          <div className="flex flex-col gap-6">
            {result.alternateMatches.length > 0 && (
              <AlternateMatchesBanner matches={result.alternateMatches} onInspect={inspectId} />
            )}
            <PrimarySection primary={result.primary} />
            <RelatedSection groups={result.related} expandedGroups={expandedGroups} onToggleGroup={toggleGroup} />
          </div>
        )}
      </main>
    </div>
  );
};

interface IAlternateMatchesBannerProps {
  matches: AlternateMatch[];
  onInspect: (id: string) => void;
}

/**
 * Names the rows a short id matched besides the one on screen, each re-running
 * the lookup on its whole id. The count includes the primary, which is not in
 * `matches`, and reads as a floor because the resolver caps how many rows it
 * fetches per table.
 */
const AlternateMatchesBanner = ({ matches, onInspect }: IAlternateMatchesBannerProps) => (
  <div className="border-fg-warning-primary bg-bg-secondary flex flex-col gap-1.5 border-l-2 px-3 py-2 text-xs">
    <span className="text-fg-warning-primary">
      That short id matches at least {matches.length + 1} rows. Showing the first — open another to inspect it instead.
    </span>
    <div className="flex flex-col items-start gap-1">
      {matches.map((object) => (
        <button
          key={`${object.type}:${object.id}`}
          type="button"
          onClick={() => onInspect(object.id)}
          className="text-fg-brand-primary hover:text-fg-brand-secondary focus-visible:outline-border-brand font-mono underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:-outline-offset-1"
        >
          {object.type} · {object.id}
        </button>
      ))}
    </div>
  </div>
);

interface IPrimarySectionProps {
  primary: InspectedObject;
}

const PrimarySection = ({ primary }: IPrimarySectionProps) => (
  <section className="flex flex-col gap-2">
    <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">
      Primary · <span className="text-fg-brand-primary">{primary.type}</span>
    </h2>
    <ObjectTable object={primary} />
  </section>
);

interface IRelatedSectionProps {
  groups: RelatedGroup[];
  expandedGroups: Set<string>;
  onToggleGroup: (type: string) => void;
}

const RelatedSection = ({ groups, expandedGroups, onToggleGroup }: IRelatedSectionProps) => {
  if (groups.length === 0) {
    return <div className="text-fg-tertiary text-xs italic">No related rows for this object.</div>;
  }
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">
        Related · {groups.length} {groups.length === 1 ? 'group' : 'groups'}
      </h2>
      <div className="flex flex-col gap-2">
        {groups.map((group) => (
          <RelatedGroupSection
            key={group.type}
            group={group}
            isExpanded={expandedGroups.has(group.type)}
            onToggle={() => onToggleGroup(group.type)}
          />
        ))}
      </div>
    </section>
  );
};

interface IRelatedGroupSectionProps {
  group: RelatedGroup;
  isExpanded: boolean;
  onToggle: () => void;
}

const RelatedGroupSection = ({ group, isExpanded, onToggle }: IRelatedGroupSectionProps) => {
  // `group.count` is the total matching-row count; `group.objects.length` is
  // capped at the per-table fetch limit on the server. Surface "X of Y" when
  // they diverge so operators know more rows exist beyond the rendered list.
  const isTruncated = group.objects.length < group.count;
  const countLabel = isTruncated
    ? `showing ${group.objects.length} of ${group.count}`
    : `${group.count} ${group.count === 1 ? 'row' : 'rows'}`;
  return (
    <div className="border-border-primary border">
      <button
        type="button"
        onClick={onToggle}
        className="hover:bg-bg-secondary flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="flex items-baseline gap-2">
          <span className="text-fg-primary text-sm font-medium">{group.type}</span>
          <span className="text-fg-tertiary font-mono text-xs">{countLabel}</span>
        </span>
        <span className="text-fg-tertiary font-mono text-xs">{isExpanded ? '−' : '+'}</span>
      </button>
      {isExpanded && (
        <div className="border-border-primary flex flex-col gap-3 border-t px-3 py-3">
          {group.objects.map((object) => (
            <ObjectTable key={object.id} object={object} />
          ))}
        </div>
      )}
    </div>
  );
};

interface IObjectTableProps {
  object: InspectedObject;
}

const ObjectTable = ({ object }: IObjectTableProps) => {
  const entries = Object.entries(object.data).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="border-border-primary border">
      <div className="border-border-primary flex items-baseline gap-2 border-b px-3 py-2">
        <span className="text-fg-tertiary text-xs uppercase tracking-wider">{object.type}</span>
        <span className="text-fg-secondary font-mono text-xs">{object.id}</span>
      </div>
      <Table>
        <Table.Body>
          {entries.map(([key, value]) => (
            <Table.Row key={key}>
              <Table.Cell muted>{key}</Table.Cell>
              {/* Every id in the value links onward, so an operator can walk a
                  row's foreign keys without copying one out by hand. */}
              <Table.Cell mono>
                <InspectLinkedText text={formatValue(value)} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

/**
 * Renders a single column value for the row table. The inspector returns
 * raw row data through the `Object` JSON scalar, so values can be strings,
 * numbers, booleans, null, or nested JSON. We stringify nested objects
 * inline rather than rendering a tree — keeps the UI compact and the
 * payload diffable when the operator is comparing two rows side-by-side.
 *
 * Returns a string rather than nodes on purpose: the caller runs the result
 * through {@link InspectLinkedText}, which finds ids anywhere in it, including
 * inside a stringified nested object.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

export default ObjectInspectorScene;
